import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10 } from "styled-components";
import React from "react";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Wrap } from "~/features/ui/wrap";
import { type Mode } from "~/features/ui/get-theme-style";
import { Headline } from "~/features/ui/headline";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
import { createLogoWallPresentation, createMarqueeRowModel } from "./model";
const MAX_LOGOS = 28;
// Duration of the ease when the marquee slows to a stop / spins back up (ms).
const RAMP_MS = 700;
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

// Smoothly ramps every marquee row's playback rate toward `target` (1 = full speed, 0 = stopped),
// so hover stops with an ease instead of snapping. Uses the Web Animations API on the CSS animation.
function useMarqueeRamp() {
  const ref = React.useRef<HTMLElement>(null);
  const rafRef = React.useRef<number>(0);
  const rampTo = React.useCallback((target: number) => {
    const tracks = ref.current?.querySelectorAll<HTMLElement>(".marquee-track");
    if (!tracks?.length) return;
    const anims = [...tracks].flatMap(t => t.getAnimations());
    if (!anims.length) return; // e.g. prefers-reduced-motion — nothing to ramp

    const from = anims.map(a => a.playbackRate);
    const start = performance.now();
    cancelAnimationFrame(rafRef.current);
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / RAMP_MS);
      const e = easeOutCubic(p);
      anims.forEach((a, i) => {
        a.playbackRate = from[i]! + (target - from[i]!) * e;
      });
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);
  React.useEffect(() => () => cancelAnimationFrame(rafRef.current), []);
  return {
    ref,
    rampTo
  };
}
const Track = _styled.div.withConfig({
  componentId: "sc-sf-x1h7dk-0"
})<{
  $reverse?: boolean;
  $duration: number;
}>`
  ${{
  "display": "flex",
  "width": "max-content",
  "alignItems": "center"
}}
  animation: logo-marquee ${p => p.$duration}s linear infinite;
  animation-direction: ${p => p.$reverse ? "reverse" : "normal"};

  @keyframes logo-marquee {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-50%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
export type LogoWallBlockViewProps<TLogo = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getLogoKey?: (logo: TLogo, index: number) => React.Key;
  gallery?: Array<TLogo | null | undefined> | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  isHighlighted?: boolean | null;
  mode?: string | null;
  renderLogo: (logo: TLogo, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
function MarqueeRow<TLogo>({
  logos,
  renderLogo,
  getLogoKey,
  reverse
}: {
  logos: TLogo[];
  renderLogo: (logo: TLogo, index: number) => React.ReactNode;
  getLogoKey?: (logo: TLogo, index: number) => React.Key;
  reverse?: boolean;
}) {
  const {
    accessibleTileCount,
    durationSeconds,
    tiles
  } = createMarqueeRowModel(logos);
  return <_StyledDiv $_css={[{
    "width": "100%",
    "overflow": "hidden"
  }]}>
      <Track className="marquee-track" $reverse={reverse} $duration={durationSeconds}>
        {tiles.map((logo, idx) => <_StyledDiv2 key={getLogoKey ? `${String(getLogoKey(logo, idx))}-${idx}` : idx} aria-hidden={idx >= accessibleTileCount || undefined} $_css2={[{
        "display": "flex",
        "flexShrink": "0",
        "alignItems": "center",
        "justifyContent": "center"
      }, {
        "height": "6.4rem",
        "width": "12.8rem",
        "@media (min-width: 1024px)": {
          "height": "12rem",
          "width": "24rem"
        }
      }, {
        "& figure": {
          "height": "100%",
          "width": "auto",
          "maxWidth": "100%"
        },
        "& img": {
          "height": "100%",
          "width": "auto",
          "maxWidth": "100%",
          "objectFit": "contain"
        }
      }]}>
            {renderLogo(logo, idx)}
          </_StyledDiv2>)}
      </Track>
    </_StyledDiv>;
}
export function LogoWallBlockView<TLogo = unknown>({
  anchor,
  getLogoKey,
  gallery = [],
  hasTopDivider = true,
  headline,
  isHighlighted = false,
  mode = "light",
  renderLogo,
  sectionProps,
  theme = "white"
}: LogoWallBlockViewProps<TLogo>) {
  const isLg = useMediaQuery(up("lg"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const {
    ref,
    rampTo
  } = useMarqueeRamp();
  const allLogos = (gallery ?? []).filter((item): item is TLogo => Boolean(item));
  const [logos, setLogos] = React.useState(() => allLogos.slice(0, MAX_LOGOS));
  React.useEffect(() => {
    const shuffled = [...allLogos];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = shuffled[i]!;
      shuffled[i] = shuffled[j]!;
      shuffled[j] = temp;
    }
    setLogos(shuffled.slice(0, MAX_LOGOS));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasItems(logos)) {
    return null;
  }
  const presentation = createLogoWallPresentation({
    headline,
    isLarge: isLg,
    logos,
    prefersReducedMotion
  });
  return <_StyledSection {...sectionProps} ref={ref} data-logo-wall="true" data-theme={mode as Mode} onMouseEnter={() => rampTo(0)} onMouseLeave={() => rampTo(1)} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css3={[{
    "position": "relative",
    "zIndex": "10",
    "backgroundColor": "var(--bg-primary)",
    "paddingTop": "var(--spacing-2xl)",
    "paddingBottom": "var(--spacing-2xl)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, theme === "grey" && {
    "backgroundColor": "var(--bg-tertiary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <_StyledDiv3 $_css4={[{
        "display": "flex",
        "flexDirection": "column",
        "alignItems": "center",
        "gap": "var(--spacing-3xl)",
        "paddingTop": "var(--spacing-2xl)"
      }]}>
          {presentation.headline ? <_StyledHeadline type="h2" text={presentation.headline} /> : null}
          {presentation.staticGrid ? <_StyledDiv4 $_css5={[{
          "display": "grid",
          "width": "100%",
          "gridTemplateColumns": "repeat(2, minmax(0, 1fr))",
          "gap": "2.4rem",
          "@media (min-width: 640px)": {
            "gridTemplateColumns": "repeat(3, minmax(0, 1fr))"
          },
          "@media (min-width: 1024px)": {
            "gridTemplateColumns": "repeat(5, minmax(0, 1fr))"
          }
        }]}>
              {logos.map((logo, idx) => <_StyledDiv5 key={getLogoKey ? getLogoKey(logo, idx) : idx} $_css6={[{
            "display": "flex",
            "height": "6.4rem",
            "alignItems": "center",
            "justifyContent": "center",
            "@media (min-width: 1024px)": {
              "height": "12rem"
            }
          }, {
            "& figure": {
              "height": "100%",
              "width": "auto",
              "maxWidth": "100%"
            },
            "& img": {
              "height": "100%",
              "width": "auto",
              "maxWidth": "100%",
              "objectFit": "contain"
            }
          }]}>
                  {renderLogo(logo, idx)}
                </_StyledDiv5>)}
            </_StyledDiv4> : <_StyledDiv6 $_css7={[{
          "display": "flex",
          "width": "100%",
          "flexDirection": "column",
          "gap": "2.4rem"
        }]}>
              {presentation.rows.map((row, idx) => hasItems(row) ? <MarqueeRow key={idx} logos={row} renderLogo={renderLogo} getLogoKey={getLogoKey} reverse={presentation.directions[idx]} /> : null)}
            </_StyledDiv6>}
        </_StyledDiv3>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-x1h7dk-1"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-x1h7dk-2"
})`${p => p.$_css2}`;
var _StyledSection = _styled4("section").withConfig({
  componentId: "sc-sf-x1h7dk-3"
})`${p => p.$_css3}`;
var _StyledWrap = _styled5(Wrap).withConfig({
  componentId: "sc-sf-x1h7dk-4"
})({
  "borderWidth": "0px"
});
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-x1h7dk-5"
})`${p => p.$_css4}`;
var _StyledHeadline = _styled7(Headline).withConfig({
  componentId: "sc-sf-x1h7dk-6"
})({
  "textAlign": "center",
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "color": "var(--text-primary)",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled8("div").withConfig({
  componentId: "sc-sf-x1h7dk-7"
})`${p => p.$_css5}`;
var _StyledDiv5 = _styled9("div").withConfig({
  componentId: "sc-sf-x1h7dk-8"
})`${p => p.$_css6}`;
var _StyledDiv6 = /*#__PURE__*/_styled10("div").withConfig({
  displayName: "logo-wall___StyledDiv6",
  componentId: "sc-t9hzw6-0"
})(["", ""], p => p.$_css7);